/**
 * Whether the player is sitting down rather than standing.
 *
 * Modelled directly on this file's sibling, `Handedness.ts`: a module-level setting holding
 * the current value, read from `localStorage` at startup and written back on change inside a
 * try/catch — private browsing refuses storage, and the setting still has to apply for that
 * session — with listeners notified so anything already running can follow a change made
 * after the session has started. Resolved fresh on every read (`.value`) rather than cached,
 * the same reason `Handedness.ts` does it: it is what lets the entry screen flip the setting
 * live while the XR session is already built.
 *
 * It is chosen here, not inferred from the size of anyone's play area — a small guardian
 * does not mean someone wants to sit, and someone with an eight-metre room may still prefer
 * to. See plan/play-area.md, "Seated use".
 *
 * Unlike handedness, this setting does not need a binding-layer seam spread across many call
 * sites — the interaction model (teleport and snap-turn, both already virtual, never reading
 * the floor; a tray anchored to the off hand's own position rather than to body or floor
 * height) never had a standing assumption built into it.
 *
 * `XrManager` used to read this too, to suppress the play-area outline and fall recentre
 * through to the room's spawn point. Both were removed after a headset test found the
 * outline drawn from a one-shot room-space snapshot that never tracked the player
 * teleporting — see plan/play-area.md, "Removed after a headset test" — leaving `Splash`, for
 * the toggle itself, as the only reader for a while.
 *
 * `XrManager` reads it again as of M9, for an unrelated reason: floating the seated eye
 * height to a standing person's — see plan/play-area.md, "Floating the seated viewpoint". That
 * used to be a second reason for `.value` to have a second call site: `HandTeleport` needed the
 * same figure for its own, separate hands-only jump, and got it from `XrManager` rather than
 * reading `.value` itself. `HandTeleport` is gone along with the rest of hand tracking
 * (2026-08-10), so `XrManager` is now this setting's only reader, not merely its only *other*
 * one — one call site, same as it was, for a simpler reason than before.
 *
 * That offset used to be re-established on recentre too, not just on session entry, which
 * gave a seated person a way to recover from a changed chair or a slump mid-session. Recentre
 * itself is gone — its off-hand button collided with the assistant's talk binding and was
 * removed in favour of it (plan/ai-in-room.md) — and nothing replaces that recovery path yet;
 * see `XrManager`'s own `seatedEyeOffset` doc comment.
 */

export const STORAGE_KEY = 'tunet:seated';

class SeatedSetting {
  private stored: boolean = loadStored();
  private readonly listeners = new Set<(seated: boolean) => void>();

  get value(): boolean {
    return this.stored;
  }

  set(seated: boolean): void {
    if (seated === this.stored) {
      return;
    }

    this.stored = seated;

    try {
      localStorage.setItem(STORAGE_KEY, seated ? 'true' : 'false');
    } catch {
      // Private browsing refuses storage. The setting still applies for this session.
    }

    for (const listener of this.listeners) {
      listener(seated);
    }
  }

  /** Notified when the setting changes, so live bindings can follow it. Returns a disposer. */
  onChanged(listener: (seated: boolean) => void): () => void {
    this.listeners.add(listener);

    return () => this.listeners.delete(listener);
  }
}

function loadStored(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    // Storage unavailable; fall through to the default.
    return false;
  }
}

export const seated = new SeatedSetting();
